import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  GreyGrey30,
  GreyGrey80,
  GreyWhite,
  Primary13SemiBold,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import {
  CustomerFormFields,
  CustomerFormFieldsData,
  useCustomerFormFields
} from 'Customer/components/CustomerFormFields'
import { CustomerSnapshot } from '../../types'
import { REVIEW_FORM_PANEL_WIDTH } from '../../constants'
import { useIsMobile } from 'hooks/useIsMobile'
import {
  applyFormDataToCustomer,
  customerSnapshotToFormData
} from '../../customerInfoDiff'
import { ReviewStep } from './steps'

/**
 * Right pane of the review modal: the shared customer form. It's the same
 * `CustomerFormFields` used by the Edit/Create Customer drawers and the contract
 * intake review, so it handles country-aware billing fields and dynamic contacts
 * for free.
 *
 * It stays mounted across steps and only toggles which sections show, so a single
 * form instance owns all edits — navigating between steps never loses them.
 * Fixed width so it sits in the layout's `auto` column while the preview pane
 * flexes to fill the rest.
 */
export const CustomerChangeEditorPanel = ({
  step,
  originalCustomer,
  proposedCustomer,
  onCustomerChange,
  onValidationStateChange,
  onActiveContactIndexChange,
  activeContactIndex,
  showValidationErrors,
  requirePrimaryContact,
  requireContacts
}: {
  step: ReviewStep
  /**
   * The customer record before the agent's proposal. Edits are applied onto this
   * so fields the review can't surface (alias, website, domain, tax status) keep
   * their original values rather than the agent's unreviewed proposal.
   */
  originalCustomer: CustomerSnapshot
  /** The agent's proposed snapshot — seeds the form's initial values. */
  proposedCustomer: CustomerSnapshot
  /** Fires on every edit with the original plus the operator's reviewed edits. */
  onCustomerChange: (customer: CustomerSnapshot) => void
  onValidationStateChange: (isValid: boolean) => void
  /**
   * Fires with the index of the contact whose field just gained focus, so the
   * preview can ring the matching card. Only fires for contact fields — focus
   * moving elsewhere leaves the previously focused contact ringed. The parent
   * maps the index to a contact id against the live edited snapshot.
   */
  onActiveContactIndexChange: (index: number) => void
  /**
   * Index of the contact whose card to ring. `null` (before the first focus)
   * rings the first contact.
   */
  activeContactIndex: number | null
  showValidationErrors: boolean
  /**
   * Whether the form must keep a PRIMARY contact among the contacts it shows.
   * False when the roster's primary is an unshown unchanged contact restored
   * on confirm (see `CustomerInfoReviewContent`) — the form must not then
   * require, or silently promote, a second primary among the shown subset.
   */
  requirePrimaryContact: boolean
  /**
   * Whether the form must show at least one contact. False when restored
   * contacts keep the submitted roster non-empty — a change that touched no
   * contact then shows an empty (but valid) contacts form.
   */
  requireContacts: boolean
}) => {
  const isMobile = useIsMobile()

  // Derived from the proposed snapshot (a stable source), never from the
  // onCustomerChange output, so the form doesn't reinitialise on every keystroke.
  const initialData = useMemo(
    () => customerSnapshotToFormData(proposedCustomer),
    [proposedCustomer]
  )

  const handleChange = useCallback(
    (data: CustomerFormFieldsData) => {
      onCustomerChange(applyFormDataToCustomer(originalCustomer, data))
    },
    [originalCustomer, onCustomerChange]
  )

  const isCustomerStep = step === 'customer'

  // The hook (not just the component) stays mounted across steps, so a single
  // useForm instance owns all edits — navigating between steps never loses
  // them. Validation follows the step: only the rendered sections' validators
  // run, so the hidden step's fields never block the confirm button. Alias,
  // tax status and tax registration are out of scope for this review: there's
  // no field to surface them and they're preserved from the original record on
  // confirm (see the diff engine's `DETAIL_FIELDS` and
  // `applyFormDataToCustomer`), so their sections are never rendered nor
  // validated.
  const { fields } = useCustomerFormFields({
    initialData,
    onChange: handleChange,
    onValidationStateChange,
    showValidationErrors,
    customerId: originalCustomer.id,
    sectionValidation: isCustomerStep
      ? {
          accountInformation: true,
          billingInformation: true,
          contacts: false,
          tax: false
        }
      : {
          accountInformation: false,
          billingInformation: false,
          contacts: { requirePrimaryContact, requireContacts },
          tax: false
        }
  })

  // Autofocus the most likely first field on open and on every step change (the
  // form stays mounted, so this keys off the step, not mount): the first
  // contact's Full name on the contacts step, else the first field (the company
  // name field). `preventScroll` keeps the top of the form in view.
  const formRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const form = formRef.current
    const firstContactName = isCustomerStep
      ? null
      : form?.querySelector<HTMLElement>(
          '[data-testid$=".contacts.contact.0.name"]'
        )
    const target =
      firstContactName ?? form?.querySelector<HTMLElement>('input, textarea')
    target?.focus({ preventScroll: true })
    // Ring the first contact's card right away. The focusin listener below only
    // attaches after this effect runs, so it misses the programmatic focus above
    // on the initial render — report the index directly instead of relying on it.
    if (firstContactName) {
      onActiveContactIndexChange(0)
    }
  }, [isCustomerStep, onActiveContactIndexChange])

  // Track which contact is being edited (via the `…contacts.contact.<index>.…`
  // test id) so the preview can ring its card. Listen on the document, not the
  // form, so focus moving into a contact field from anywhere retargets the ring;
  // `focusin` covers tabbing and autofocus, `mousedown` covers clicks onto
  // non-focusable areas that emit no focusin. Interactions that aren't on a
  // contact field are ignored, so the previously focused contact stays ringed.
  useEffect(() => {
    const contactIndexOf = (element: EventTarget | null): number | null => {
      const contactField = (
        element as HTMLElement | null
      )?.closest<HTMLElement>('[data-testid*=".contacts.contact."]')
      const index = contactField?.dataset.testid?.match(
        /\.contacts\.contact\.(\d+)\b/
      )?.[1]

      return index === undefined ? null : Number(index)
    }

    const handleInteraction = (event: FocusEvent | MouseEvent) => {
      // Interactions inside a portalled overlay — the confirm dialog
      // (`role="dialog"`) or a contact's kebab "Remove contact" menu
      // (`role="menu"`) — bubble to the document but must not retarget the ring:
      // clicking "Remove contact" would otherwise ring the very contact being
      // deleted. These overlays render outside the review modal's plain-div
      // subtree and carry an ARIA role, so skip them.
      const target = event.target as HTMLElement | null
      if (
        target?.closest('[role="dialog"], [role="alertdialog"], [role="menu"]')
      ) {
        return
      }

      // Only retarget the ring when the interaction lands on a contact field.
      // Interactions elsewhere leave the previously focused contact ringed, so
      // the preview keeps showing that contact rather than blanking when focus
      // moves off the contact fields.
      const contactIndex = contactIndexOf(event.target)
      if (contactIndex !== null) {
        onActiveContactIndexChange(contactIndex)
      }
    }

    document.addEventListener('focusin', handleInteraction)
    document.addEventListener('mousedown', handleInteraction)
    return () => {
      document.removeEventListener('focusin', handleInteraction)
      document.removeEventListener('mousedown', handleInteraction)
    }
  }, [onActiveContactIndexChange])

  return (
    <Grid
      templateRows="auto 1fr"
      height="100%"
      // On mobile this pane is shown alone (the preview is dropped) in a
      // full-width container, so the fixed panel width would overflow the
      // viewport — fill the width instead.
      width={isMobile ? '100%' : REVIEW_FORM_PANEL_WIDTH}
      overflow="hidden"
      backgroundColor={GreyWhite}
    >
      {/* Single, always-active "Edit" tab, matching the quote-review details
          tab bar. Static (no click handling) because this pane only ever shows
          the edit form. */}
      <GridItem
        display="flex"
        gap="4px"
        padding="0 12px"
        borderBottom={`1px solid ${GreyGrey30}`}
      >
        <Flex
          height="40px"
          padding="2px 8px 0"
          borderBottom={`2px solid ${GreyGrey80}`}
          color={GreyGrey80}
          {...Primary13SemiBold}
          alignItems="center"
          justifyContent="center"
          data-testid="customerInfoReview.editTab"
        >
          Edit
        </Flex>
      </GridItem>

      <GridItem overflowY="auto" padding="24px">
        <Flex flexDirection="column" gap="16px">
          <Box ref={formRef}>
            {/* Step 1 reviews the customer details — the company (legal) name
                and the billing address; step 2 reviews the contacts. */}
            <CustomerFormFields>
              {isCustomerStep ? (
                <>
                  <CustomerFormFields.AccountInformation
                    data-testid="customerInfoReview.form.accountInformation"
                    legalNameField={fields.legalName}
                  />
                  <CustomerFormFields.BillingInformation
                    data-testid="customerInfoReview.form.billingInformation"
                    addressFields={fields}
                  />
                </>
              ) : (
                <CustomerFormFields.Contacts
                  data-testid="customerInfoReview.form.contacts"
                  contactsField={fields.contacts}
                  showInvoiceRecipientToggle
                  requirePrimaryContact={requirePrimaryContact}
                  // Ring the contact card being edited, matching the preview's
                  // active contact. `null` (before the first focus) rings the
                  // first contact.
                  activeContactIndex={activeContactIndex ?? 0}
                />
              )}
            </CustomerFormFields>
          </Box>
        </Flex>
      </GridItem>
    </Grid>
  )
}
